import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { initSentry } from "./config/sentryConfig";
import * as Sentry from "@sentry/react";
import Navbar from "./components/Navbar";
import EventForm from "./components/EventForm";
import Tournaments from "./pages/Tournaments/Tournaments";
import Tournament from "./pages/Tournament/Tournament";
import TournamentContacts from "./pages/Tournament/TournamentContacts";
import TournamentEventRulesPage from "./pages/Tournament/TournamentEventRulesPage";

import SignUp from "./pages/SignUp/SignUp";
import SignIn from "./pages/SignIn/SignIn";
import ForgotPassword from "./pages/ForgotPassword/ForgotPassword";
import ResetPassword from "./pages/ResetPassword/ResetPassword";
import CreateTournament from "./pages/CreateTournament/CreateTournament";
import TournamentResults from "./pages/Results/Results";
import { AuthProvider } from "./context/AuthContext";
import BowlerAverage from "./pages/BowlerAverage/BowlerAverages";
import LandingPage from "./pages/LandingPage";
import BowlingTournamentSoftware from "./pages/BowlingTournamentSoftware/BowlingTournamentSoftware";
import BowlingTournamentAiAssist from "./pages/BowlingTournamentAiAssist/BowlingTournamentAiAssist";
import BowlingBracketSoftware from "./pages/BowlingBracketSoftware/BowlingBracketSoftware";
import BowlingTournamentSignups from "./pages/BowlingTournamentSignups/BowlingTournamentSignups";
import BowlingTournamentScoring from "./pages/BowlingTournamentScoring/BowlingTournamentScoring";
import BowlingTournamentPayouts from "./pages/BowlingTournamentPayouts/BowlingTournamentPayouts";
import BowlingTournamentCardPayments from "./pages/BowlingTournamentCardPayments/BowlingTournamentCardPayments";
import BowlingTournamentAverages from "./pages/BowlingTournamentAverages/BowlingTournamentAverages";
import BowlingTournamentAverageImport from "./pages/BowlingTournamentAverageImport/BowlingTournamentAverageImport";
import BowlingTournamentSignupSheetImport from "./pages/BowlingTournamentSignupSheetImport/BowlingTournamentSignupSheetImport";
import BowlingTournamentRecapSheetImport from "./pages/BowlingTournamentRecapSheetImport/BowlingTournamentRecapSheetImport";
import BowlingTournamentCheckExports from "./pages/BowlingTournamentCheckExports/BowlingTournamentCheckExports";
import Plans from "./pages/Plans/Plans";
import ProductShots from "./pages/ProductShots/ProductShots";
import PayoutCalculator from "./pages/PayoutCalculator/PayoutCalculator";
import Squads from "./pages/Squads/Squads";
import TournamentScoring from "./pages/TournamentScoring/TournamentScoring";
import TournamentBrackets from "./pages/TournamentBrackets/TournamentBrackets";
import TournamentFinance from "./pages/TournamentFinance/TournamentFinance";
import TournamentSetup from "./pages/TournamentSetup/TournamentSetup";
import TournamentSettings from "./pages/TournamentSettings/TournamentSettings";
import {
  RedirectAdmin,
  RedirectFinanceIndex,
  RedirectPayoutDashboard,
  RedirectQrCodes,
  RedirectResults,
} from "./pages/TournamentAdmin/legacyRedirects";
import Prizes from "./pages/Prizes/Prizes";
import PayoutTablePage from "./pages/PayoutTable/PayoutTable";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TournamentProvider } from "./context/TournamentContext";
import Teams from "./pages/Teams/Teams";
import TeamEdit from "./pages/TeamEdit/TeamEdit";
import PrivacyPolicy from "./pages/PrivacyPolicy/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService/TermsOfService";
import Disclaimer from "./pages/Disclaimer/Disclaimer";
import RefundPolicy from "./pages/RefundPolicy/RefundPolicy";
import DataProcessingAgreement from "./pages/DataProcessingAgreement/DataProcessingAgreement";
import DataRetentionPolicy from "./pages/DataRetentionPolicy/DataRetentionPolicy";
import AccessibilityStatement from "./pages/AccessibilityStatement/AccessibilityStatement";
import FoundersNote from "./pages/FoundersNote/FoundersNote";
import Support from "./pages/Support/Support";
import Contact from "./pages/Support/Contact";
import ArticleViewer from "./pages/Support/ArticleViewer";
import Footer from "./components/Footer/Footer";
import { CookieProvider } from "./context/CookieContext";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import HeadScript from "./components/Analytics/HeadScript";
import GoogleAnalyticsScript from "./components/Analytics/GoogleAnalyticsScript";
import { GoogleMapsProvider } from "./context/GoogleMapsContext";
import { setupSessionMonitoring } from "./utility/security/sessionUtils";
import ProtectedRoute from "./components/ProtectedRoute";
import Profile from "./pages/Profile/Profile";
import OrganizationLayout from "./components/organizations/OrganizationLayout";
import OrganizationDashboard from "./components/organizations/Dashboard/OrganizationDashboard";
import MemberList from "./components/organizations/Members/MemberList";
import BowlerList from "./components/organizations/Bowlers/BowlerList";
import OrganizationProfile from "./components/organizations/Profile/OrganizationProfile";
import CardPayments from "./components/organizations/CardPayments/CardPayments";
import Plan from "./components/organizations/Plan/Plan";
import OrganizationTournaments from "./components/organizations/Tournaments/OrganizationTournaments";
import OrganizationPayoutTables from "./components/organizations/PayoutTables/OrganizationPayoutTables";
import OrganizationPayoutTableEditor from "./components/organizations/PayoutTables/OrganizationPayoutTableEditor";
import OrganizationCheckTemplates from "./components/organizations/CheckTemplates/OrganizationCheckTemplates";
import CheckTemplateEditor from "./components/organizations/CheckTemplates/CheckTemplateEditor";
import OrganizationBowlingCenters from "./components/organizations/BowlingCenters/OrganizationBowlingCenters";
import AverageCatalogs from "./components/organizations/AverageCatalogs/AverageCatalogs";
import AverageCatalogDetail from "./components/organizations/AverageCatalogs/AverageCatalogDetail";
import AverageCatalogsGate from "./components/organizations/AverageCatalogs/AverageCatalogsGate";
import CheckTemplatesGate from "./components/organizations/CheckTemplates/CheckTemplatesGate";
import DPAAcceptance from "./pages/DPAAcceptance/DPAAcceptance";
import InviteAccept from "./pages/InviteAccept/InviteAccept";
import Invites from "./pages/Invites/Invites";
import AuthCallback from "./pages/AuthCallback";
import AuthRedirectHandler from "./components/AuthRedirectHandler";
import { OrganizationProvider } from "./context/OrganizationContext";
import { PermissionsProvider } from "./context/PermissionsContext";
import { NotificationProvider } from "./context/NotificationContext";
import TournamentLayout from "./components/Layout/TournamentLayout";
import { Analytics } from "@vercel/analytics/react";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import NotFound from "./pages/NotFound/NotFound";
import UUIDValidationWrapper from "./components/UUIDValidation/UUIDValidationWrapper";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import DefaultSeo from "./components/SEO/DefaultSeo";
import SignupLanding from "./components/Signup/SignupLanding";
import SignupCart from "./components/Signup/SignupCart";
import SignupEdit from "./components/Signup/SignupEdit";
import SignupDashboard from "./components/Admin/SignupDashboard";
import SignupReview from "./components/Admin/SignupReview";
import AdminSignupEditComponent from "./components/Admin/SignupEdit";
import UserSignupDashboard from "./pages/UserSignupDashboard/UserSignupDashboard";

if (typeof window !== "undefined") {
  initSentry();
}

// Clean up the API key by removing any trailing semicolons and whitespace
const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim().replace(/[;\s]+$/, "");

const libraries = ["places"];

const DefaultRouter = ({ children }) => (
  <BrowserRouter>{children}</BrowserRouter>
);

const App = ({ Router = DefaultRouter, helmetContext }) => {
  useEffect(() => {
    setupSessionMonitoring();
  }, []);

  return (
    <HelmetProvider context={helmetContext}>
      <AuthProvider>
        <OrganizationProvider>
          <NotificationProvider>
            <Router>
              <DefaultSeo />
              <GoogleMapsProvider>
                <TournamentProvider>
                  <CookieProvider>
                    <ErrorBoundary>
                      <HeadScript />
                      <GoogleAnalyticsScript />
                      <ScrollToTop />
                      <div className="flex flex-col min-h-screen">
                        <AuthRedirectHandler />
                        <Navbar />
                        <main className="flex-grow overflow-x-clip max-w-full min-w-0">
                          <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route
                              path="/bowling-tournament-software"
                              element={<BowlingTournamentSoftware />}
                            />
                            <Route
                              path="/bowling-tournament-ai-assist"
                              element={<BowlingTournamentAiAssist />}
                            />
                            <Route
                              path="/bowling-bracket-software"
                              element={<BowlingBracketSoftware />}
                            />
                            <Route
                              path="/bowling-payout-calculator"
                              element={<PayoutCalculator />}
                            />
                            <Route
                              path="/bowling-tournament-signups"
                              element={<BowlingTournamentSignups />}
                            />
                            <Route
                              path="/bowling-tournament-scoring"
                              element={<BowlingTournamentScoring />}
                            />
                            <Route
                              path="/bowling-tournament-payouts"
                              element={<BowlingTournamentPayouts />}
                            />
                            <Route
                              path="/bowling-tournament-card-payments"
                              element={<BowlingTournamentCardPayments />}
                            />
                            <Route
                              path="/bowling-tournament-averages"
                              element={<BowlingTournamentAverages />}
                            />
                            <Route
                              path="/bowling-tournament-average-import"
                              element={<BowlingTournamentAverageImport />}
                            />
                            <Route
                              path="/bowling-tournament-signup-sheet-import"
                              element={<BowlingTournamentSignupSheetImport />}
                            />
                            <Route
                              path="/bowling-tournament-recap-sheet-import"
                              element={<BowlingTournamentRecapSheetImport />}
                            />
                            <Route
                              path="/bowling-tournament-check-exports"
                              element={<BowlingTournamentCheckExports />}
                            />
                            <Route path="/pricing" element={<Plans />} />
                            <Route
                              path="/internal/product-shots"
                              element={<ProductShots />}
                            />
                            <Route
                              path="/tournaments"
                              element={<Tournaments />}
                            />
                            <Route path="/events" element={<EventForm />} />
                            <Route
                              path="/tournament/:tournamentId/squads"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <Squads />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/scoring"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <TournamentScoring />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/scoring/:tab"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <TournamentScoring />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/brackets"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <TournamentBrackets />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />

                            <Route
                              path="/tournament/:tournamentId/admin"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <RedirectAdmin />
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/setup"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <ProtectedRoute requireOrganization={true}>
                                    <TournamentLayout>
                                      <TournamentSetup />
                                    </TournamentLayout>
                                  </ProtectedRoute>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/settings"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <ProtectedRoute requireOrganization={true}>
                                    <TournamentLayout>
                                      <TournamentSettings />
                                    </TournamentLayout>
                                  </ProtectedRoute>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/finance"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <RedirectFinanceIndex />
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/finance/:tab"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <TournamentFinance />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/signup-dashboard"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <ProtectedRoute requireOrganization={true}>
                                    <TournamentLayout>
                                      <SignupDashboard />
                                    </TournamentLayout>
                                  </ProtectedRoute>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/signup-dashboard/:signupId"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <ProtectedRoute requireOrganization={true}>
                                    <TournamentLayout>
                                      <SignupReview />
                                    </TournamentLayout>
                                  </ProtectedRoute>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/signup-edit/:signupId"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <ProtectedRoute requireOrganization={true}>
                                    <TournamentLayout>
                                      <AdminSignupEditComponent />
                                    </TournamentLayout>
                                  </ProtectedRoute>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <Tournament />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/signups"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <SignupLanding />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/signups/cart"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <SignupCart />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/signups/edit/:signupId"
                              element={
                                <ProtectedRoute>
                                  <SignupEdit />
                                </ProtectedRoute>
                              }
                            />
                            <Route path="/signup" element={<SignUp />} />
                            <Route path="/signin" element={<SignIn />} />
                            <Route
                              path="/signup/dashboard"
                              element={
                                <ProtectedRoute>
                                  <UserSignupDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/forgot-password"
                              element={<ForgotPassword />}
                            />
                            <Route
                              path="/reset-password"
                              element={<ResetPassword />}
                            />
                            <Route
                              path="/profile"
                              element={
                                <ProtectedRoute>
                                  <Profile />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/create-tournament"
                              element={
                                <ProtectedRoute requireOrganization={true}>
                                  <CreateTournament />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/results"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <RedirectResults />
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/standings"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <TournamentResults />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/prizes"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <Prizes />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/rules"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <TournamentEventRulesPage />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/payout"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <PayoutTablePage />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/averages"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <BowlerAverage />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/contacts"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <TournamentContacts />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/qr-codes"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <RedirectQrCodes />
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/payout-dashboard/*"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <RedirectPayoutDashboard />
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/teams"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament"]}
                                >
                                  <TournamentLayout>
                                    <Teams />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/tournament/:tournamentId/teams/:teamId"
                              element={
                                <UUIDValidationWrapper
                                  entities={["tournament", "team"]}
                                >
                                  <TournamentLayout>
                                    <TeamEdit />
                                  </TournamentLayout>
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route
                              path="/privacy-policy"
                              element={<PrivacyPolicy />}
                            />
                            <Route
                              path="/privacy"
                              element={<Navigate to="/privacy-policy" replace />}
                            />
                            <Route
                              path="/terms-of-service"
                              element={<TermsOfService />}
                            />
                            <Route
                              path="/terms"
                              element={
                                <Navigate to="/terms-of-service" replace />
                              }
                            />
                            <Route
                              path="/disclaimer"
                              element={<Disclaimer />}
                            />
                            <Route
                              path="/refund-policy"
                              element={<RefundPolicy />}
                            />
                            <Route
                              path="/data-processing-agreement"
                              element={<DataProcessingAgreement />}
                            />
                            <Route
                              path="/data-retention-policy"
                              element={<DataRetentionPolicy />}
                            />
                            <Route
                              path="/accessibility-statement"
                              element={<AccessibilityStatement />}
                            />
                            <Route
                              path="/founders-note"
                              element={<FoundersNote />}
                            />
                            <Route path="/support" element={<Support />} />
                            <Route
                              path="/support/contact"
                              element={<Contact />}
                            />
                            {/* Dynamic Article Viewer Routes */}
                            <Route
                              path="/support/tutorials/:articlePath"
                              element={<ArticleViewer />}
                            />
                            <Route
                              path="/support/faqs/:articlePath"
                              element={<ArticleViewer />}
                            />
                            <Route
                              path="/organization"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <OrganizationDashboard />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/members"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <MemberList />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/bowlers"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <BowlerList />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/tournaments"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <OrganizationTournaments />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/bowling-centers"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <OrganizationBowlingCenters />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/average-catalogs/:catalogId"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <AverageCatalogsGate>
                                      <AverageCatalogDetail />
                                    </AverageCatalogsGate>
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/average-catalogs"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <AverageCatalogsGate>
                                      <AverageCatalogs />
                                    </AverageCatalogsGate>
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/payout-tables/:tableId"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <OrganizationPayoutTableEditor />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/payout-tables"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <OrganizationPayoutTables />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/check-templates/:templateId"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <CheckTemplatesGate>
                                      <CheckTemplateEditor />
                                    </CheckTemplatesGate>
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/check-templates"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <CheckTemplatesGate>
                                      <OrganizationCheckTemplates />
                                    </CheckTemplatesGate>
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/plan"
                              element={
                                <ProtectedRoute requireOrganization={true}>
                                  <PermissionsProvider>
                                    <OrganizationLayout>
                                      <Plan />
                                    </OrganizationLayout>
                                  </PermissionsProvider>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/organization/card-payments"
                              element={
                                <ProtectedRoute requireOrganization={true}>
                                  <PermissionsProvider>
                                    <OrganizationLayout>
                                      <CardPayments />
                                    </OrganizationLayout>
                                  </PermissionsProvider>
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/organization/profile"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <OrganizationProfile />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/organization/dpa-acceptance"
                              element={
                                <PermissionsProvider>
                                  <OrganizationLayout>
                                    <DPAAcceptance />
                                  </OrganizationLayout>
                                </PermissionsProvider>
                              }
                            />
                            <Route
                              path="/invite/:inviteId"
                              element={
                                <UUIDValidationWrapper entities={["invite"]}>
                                  <InviteAccept />
                                </UUIDValidationWrapper>
                              }
                            />
                            <Route path="/invites" element={<Invites />} />
                            <Route
                              path="/auth/callback"
                              element={<AuthCallback />}
                            />
                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </main>
                        <Footer />
                        <CookieConsent />
                        <ToastContainer
                          position="top-right"
                          autoClose={3000}
                          hideProgressBar={false}
                          newestOnTop
                          closeOnClick
                          rtl={false}
                          pauseOnFocusLoss
                          draggable
                          pauseOnHover
                          theme="light"
                        />
                        <Analytics />
                      </div>
                    </ErrorBoundary>
                  </CookieProvider>
                </TournamentProvider>
              </GoogleMapsProvider>
            </Router>
          </NotificationProvider>
        </OrganizationProvider>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
